import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { queryClient } from "..";
import { companyApi } from "../api/company-api";
import {
  setCompanyWithCollaborativesInPageByDtoAction,
  setCompanyWithCollaborativesPageAction,
  setCompanyWithCollaborativesPageAllAction,
} from "../app/reducers/company-slice";
import { store } from "../app/store";
import {
  CreateCompanyPayload,
  SearchCompanyPagePayload,
  UpdateCompanyPayload,
} from "../types/company/company-payload";
import { Confirm } from "../types/confirm";

export enum CompanyWithCollaborativesQueryKey {
  Create = "CreateCompanyWithCollaboratives",
  Update = "UpdateCompanyWithCollaboratives",
  Delete = "DeleteCompanyWithCollaboratives",
  GetById = "GetCompanyWithCollaborativesById",
  Page = "CompanyWithCollaborativesPage",
  PageAll = "CompanyWithCollaborativesPageAll",
}

export enum CompanyQueryKey {
  GetByName = "GetCompanyByName",
}

export const useCreateCompanyWithCollaborativesMutation = () => {
  return useMutation(
    CompanyWithCollaborativesQueryKey.Create,
    (payload: CreateCompanyPayload) => companyApi.create(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CompanyWithCollaborativesQueryKey.Page);
        toast.success(Confirm.Confirm);
      },
    }
  );
};

export const useUpdateCompanyWithCollaborativesMutation = () => {
  return useMutation(
    CompanyWithCollaborativesQueryKey.Update,
    (params: { id: string; payload: UpdateCompanyPayload }) => {
      return companyApi.update(params.id, params.payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CompanyWithCollaborativesQueryKey.Page);
        toast.success(Confirm.Update);
      },
    }
  );
};

export const useDeleteCompanyWithCollaborativesMutation = () => {
  return useMutation(
    CompanyWithCollaborativesQueryKey.Delete,
    (id: string) => companyApi.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(CompanyWithCollaborativesQueryKey.Page);
        toast.success(Confirm.Delete);
      },
    }
  );
};

export const useGetCompanyWithCollaborativesByIdQuery = (payload: string) => {
  return useQuery(
    [CompanyWithCollaborativesQueryKey.GetById, payload],
    () => companyApi.getById(payload),
    {
      enabled: !!payload,
      onSuccess: ({ data }) => {
        const companyWithCollaborativesDto = data;
        store.dispatch(
          setCompanyWithCollaborativesInPageByDtoAction({
            companyWithCollaborativesDto,
          })
        );
      },
    }
  );
};

export const useGetCompanyWithCollaborativesPageQuery = (
  payload: SearchCompanyPagePayload
) => {
  return useQuery(
    [CompanyWithCollaborativesQueryKey.Page, payload],
    () => companyApi.search(payload),
    {
      enabled: payload.hasOwnProperty("page"),
      onSuccess: ({ data }) => {
        const companyWithCollaborativesPageDto = data;
        store.dispatch(
          setCompanyWithCollaborativesPageAction({
            companyWithCollaborativesPageDto,
          })
        );
      },
    }
  );
};

export const useCompanyWithCollaborativesPageAllQuery = (enabled?: boolean) => {
  return useQuery(
    [CompanyWithCollaborativesQueryKey.PageAll],
    () => companyApi.searchAll(),
    {
      enabled,
      onSuccess: ({ data }) => {
        const companyWithCollaborativesPageAllDto = data;
        store.dispatch(
          setCompanyWithCollaborativesPageAllAction({
            companyWithCollaborativesPageAllDto,
          })
        );
      },
    }
  );
};

export const useGetCompanyByNameQuery = (payload: string) => {
  return useQuery(
    [CompanyQueryKey.GetByName, payload],
    () => companyApi.getByName(payload),
    {
      enabled: !!payload,
      onSuccess: ({ data }) => {
      },
    }
  );
};
